/**
 * Sprint 5 client feedback 2026-07-07 (UI Issues #5) — a single shared
 * date formatter for every listing page in the application.
 *
 * Rationale: prior renders scattered `toLocaleDateString(undefined, …)`
 * calls that picked up the browser locale. A US-locale browser
 * produced "Jul 06, 2026"; a UK-locale browser produced "06 Jul 2026".
 * The client wanted deterministic `DD MMM YYYY` (or with time
 * `DD MMM YYYY, HH:mm`) across all listing tables regardless of
 * browser locale.
 *
 * Scope
 *   - UI-side only. Backend date payloads are untouched.
 *   - Timezone: dates are converted to the user's local timezone
 *     before formatting so a UTC "2026-07-06T05:30Z" becomes
 *     "06 Jul 2026, 09:30" for an analyst on Muscat time.
 *   - Sorting / filtering / storage is unaffected — those still
 *     work off the raw ISO string.
 *
 * Do NOT use inside report bodies, PDFs, or export files unless the
 * caller is a listing-style table.
 */

const DAY_FMT = new Intl.DateTimeFormat('en-GB', { day: '2-digit' });
const MON_FMT = new Intl.DateTimeFormat('en-GB', { month: 'short' });
const YEAR_FMT = new Intl.DateTimeFormat('en-GB', { year: 'numeric' });
const TIME_FMT = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function parse(input: string | Date | null | undefined): Date | null {
  if (input == null || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format an ISO-8601 UTC (or Date) input as `DD MMM YYYY`.
 * Returns the em-dash placeholder for null / invalid inputs so table
 * cells stay uniform width.
 *   "2026-07-06T05:30:00Z"  →  "06 Jul 2026"
 *   "2026-01-29"            →  "29 Jan 2026"
 *   null                    →  "—"
 */
export function formatListingDate(
  input: string | Date | null | undefined,
): string {
  const d = parse(input);
  if (!d) return '—';
  return `${DAY_FMT.format(d)} ${MON_FMT.format(d)} ${YEAR_FMT.format(d)}`;
}

/**
 * Format as `DD MMM YYYY, HH:mm` in the user's local timezone.
 * Returns the em-dash placeholder for null / invalid inputs.
 *   "2026-07-06T05:30:00Z" (Muscat)  →  "06 Jul 2026, 09:30"
 */
export function formatListingDateTime(
  input: string | Date | null | undefined,
): string {
  const d = parse(input);
  if (!d) return '—';
  return `${DAY_FMT.format(d)} ${MON_FMT.format(d)} ${YEAR_FMT.format(d)}, ${TIME_FMT.format(d)}`;
}
